import React, { useState } from 'react';
import { FaBook, FaCode, FaCheckCircle, FaExclamationTriangle, FaLink, FaCopy, FaRocket } from 'react-icons/fa';
import toast from 'react-hot-toast';
import Container from './styles';

const WebhookDocumentation = () => {
  const [copiedSection, setCopiedSection] = useState(null);

  const copyToClipboard = (text, section) => {
    navigator.clipboard.writeText(text);
    setCopiedSection(section);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopiedSection(null), 2000);
  };

  const eventTypes = [
    {
      name: 'order.created',
      description: 'Triggered when a new order is created',
      color: '#4CAF50'
    },
    {
      name: 'order.updated',
      description: 'Triggered when order details are updated',
      color: '#2196F3'
    },
    {
      name: 'order.status_changed',
      description: 'Triggered when order status changes',
      color: '#FF9800'
    },
    {
      name: 'shipment.picked_up',
      description: 'Triggered when rider picks up the package',
      color: '#9C27B0'
    },
    {
      name: 'shipment.delivered',
      description: 'Triggered when delivery is completed',
      color: '#4CAF50'
    },
    {
      name: 'shipment.failed',
      description: 'Triggered when delivery fails',
      color: '#F44336'
    }
  ];

  const payloadExample = {
    event: "order.created",
    timestamp: 1788248693366,
    data: {
      id: "aed5dc56-9bf1-4ee0-8b23-9c4c9db4d1dd",
      companyId: "company-407119-353aae",
      customerName: "John Doe",
      customerPhone: "+234 XXX XXX XXXX",
      customerEmail: "john@example.com",
      pickupAddress: "123 Pickup Street, Lagos",
      deliveryAddress: "456 Delivery Avenue, Lagos",
      packageDescription: "Electronics - Handle with care",
      status: "PENDING",
      trackingNumber: "PL1788248693223F8EE",
      createdAt: 1788248693223,
      updatedAt: 1788248693223
    }
  };

  const signatureExample = `const crypto = require('crypto');

function verifyWebhookSignature(payload, signature, secret) {
  const hmac = crypto.createHmac('sha256', secret);
  hmac.update(JSON.stringify(payload));
  const expectedSignature = hmac.digest('hex');
  
  return crypto.timingSafeEqual(
    Buffer.from(signature),
    Buffer.from(expectedSignature)
  );
}

// Usage in your webhook endpoint
app.post('/webhook', (req, res) => {
  const signature = req.headers['x-webhook-signature'];
  const payload = req.body;
  const secret = 'your-webhook-secret';
  
  if (!verifyWebhookSignature(payload, signature, secret)) {
    return res.status(401).json({ error: 'Invalid signature' });
  }
  
  // Process the webhook
  console.log('Received event:', payload.event);
  res.status(200).json({ received: true });
});`;

  const nodeExample = `const express = require('express');
const app = express();

app.use(express.json());

app.post('/webhooks/astar', (req, res) => {
  const { event, timestamp, data } = req.body;
  
  console.log('Received webhook:', event);
  
  switch(event) {
    case 'order.created':
      // Handle new order
      console.log('New order:', data.trackingNumber);
      break;
      
    case 'shipment.delivered':
      // Handle delivery
      console.log('Delivered:', data.trackingNumber);
      break;
      
    default:
      console.log('Unhandled event:', event);
  }
  
  res.status(200).json({ received: true });
});

app.listen(3000, () => {
  console.log('Webhook server running on port 3000');
});`;

  const pythonExample = `from flask import Flask, request, jsonify
import hmac
import hashlib

app = Flask(__name__)

@app.route('/webhooks/astar', methods=['POST'])
def webhook():
    payload = request.json
    signature = request.headers.get('X-Webhook-Signature')
    
    # Verify signature
    secret = 'your-webhook-secret'
    expected_signature = hmac.new(
        secret.encode(),
        request.data,
        hashlib.sha256
    ).hexdigest()
    
    if signature != expected_signature:
        return jsonify({'error': 'Invalid signature'}), 401
    
    # Process event
    event = payload.get('event')
    data = payload.get('data')
    
    if event == 'order.created':
        print(f"New order: {data['trackingNumber']}")
    elif event == 'shipment.delivered':
        print(f"Delivered: {data['trackingNumber']}")
    
    return jsonify({'received': True}), 200

if __name__ == '__main__':
    app.run(port=3000)`;

  const phpExample = `<?php
header('Content-Type: application/json');

// Read webhook payload
$payload = file_get_contents('php://input');
$data = json_decode($payload, true);

// Verify signature
$signature = $_SERVER['HTTP_X_WEBHOOK_SIGNATURE'] ?? '';
$secret = 'your-webhook-secret';
$expectedSignature = hash_hmac('sha256', $payload, $secret);

if (!hash_equals($expectedSignature, $signature)) {
    http_response_code(401);
    echo json_encode(['error' => 'Invalid signature']);
    exit;
}

// Process event
$event = $data['event'];
$orderData = $data['data'];

switch($event) {
    case 'order.created':
        // Handle new order
        error_log("New order: " . $orderData['trackingNumber']);
        break;
        
    case 'shipment.delivered':
        // Handle delivery
        error_log("Delivered: " . $orderData['trackingNumber']);
        break;
}

http_response_code(200);
echo json_encode(['received' => true]);
?>`;

  return (
    <Container>
      <div className="doc-header">
        <div className="header-content">
          <FaBook className="header-icon" />
          <div>
            <h1>Webhook Documentation</h1>
            <p>Learn how to integrate Astar webhooks into your application</p>
          </div>
        </div>
      </div>

      <div className="doc-content">
        {/* Quick Start */}
        <section className="doc-section">
          <div className="section-header">
            <FaRocket className="section-icon" />
            <h2>Quick Start</h2>
          </div>
          <div className="section-content">
            <ol className="step-list">
              <li>
                <strong>Create a webhook endpoint</strong> in your application that can receive POST requests
              </li>
              <li>
                <strong>Go to Settings → Webhooks</strong> and create a new webhook
              </li>
              <li>
                <strong>Enter your endpoint URL</strong> (must be publicly accessible via HTTPS)
              </li>
              <li>
                <strong>Select the events</strong> you want to receive notifications for
              </li>
              <li>
                <strong>Add a secret</strong> (optional but recommended) for signature verification
              </li>
              <li>
                <strong>Test your webhook</strong> using the "Test" button
              </li>
            </ol>
          </div>
        </section>

        {/* Event Types */}
        <section className="doc-section">
          <div className="section-header">
            <FaCheckCircle className="section-icon" />
            <h2>Available Events</h2>
          </div>
          <div className="section-content">
            <p>Subscribe to any of the following events:</p>
            <div className="event-grid">
              {eventTypes.map((event) => (
                <div key={event.name} className="event-card" style={{ borderLeftColor: event.color }}>
                  <div className="event-name" style={{ color: event.color }}>
                    {event.name}
                  </div>
                  <div className="event-description">{event.description}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Payload Structure */}
        <section className="doc-section">
          <div className="section-header">
            <FaCode className="section-icon" />
            <h2>Payload Structure</h2>
          </div>
          <div className="section-content">
            <p>All webhook events follow this structure:</p>
            <div className="code-block-header">
              <span>Example Payload</span>
              <button
                className="copy-btn"
                onClick={() => copyToClipboard(JSON.stringify(payloadExample, null, 2), 'payload')}
              >
                <FaCopy /> {copiedSection === 'payload' ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <pre className="code-block">
              {JSON.stringify(payloadExample, null, 2)}
            </pre>
            
            <div className="info-box">
              <FaExclamationTriangle />
              <div>
                <strong>Important:</strong> The <code>data</code> object contains different fields depending on the event type. Always check the <code>event</code> field first.
              </div>
            </div>
          </div>
        </section>

        {/* Request Headers */}
        <section className="doc-section">
          <div className="section-header">
            <FaLink className="section-icon" />
            <h2>Request Headers</h2>
          </div>
          <div className="section-content">
            <p>Astar sends the following headers with every webhook request:</p>
            <table className="headers-table">
              <thead>
                <tr>
                  <th>Header</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><code>Content-Type</code></td>
                  <td>Always <code>application/json</code></td>
                </tr>
                <tr>
                  <td><code>User-Agent</code></td>
                  <td><code>Astar-Webhooks/1.0</code></td>
                </tr>
                <tr>
                  <td><code>X-Webhook-Signature</code></td>
                  <td>HMAC SHA-256 signature (if secret is configured)</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Signature Verification */}
        <section className="doc-section">
          <div className="section-header">
            <FaCheckCircle className="section-icon" />
            <h2>Signature Verification</h2>
          </div>
          <div className="section-content">
            <p>
              If you configure a secret for your webhook, Astar will include an 
              <code>X-Webhook-Signature</code> header. Use this to verify the request is genuine.
            </p>
            
            <div className="code-block-header">
              <span>Verification Example (Node.js)</span>
              <button
                className="copy-btn"
                onClick={() => copyToClipboard(signatureExample, 'signature')}
              >
                <FaCopy /> {copiedSection === 'signature' ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <pre className="code-block">{signatureExample}</pre>
          </div>
        </section>

        {/* Code Examples */}
        <section className="doc-section">
          <div className="section-header">
            <FaCode className="section-icon" />
            <h2>Implementation Examples</h2>
          </div>
          <div className="section-content">
            {/* Node.js */}
            <div className="example-block">
              <div className="code-block-header">
                <span>Node.js / Express</span>
                <button
                  className="copy-btn"
                  onClick={() => copyToClipboard(nodeExample, 'node')}
                >
                  <FaCopy /> {copiedSection === 'node' ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <pre className="code-block">{nodeExample}</pre>
            </div>

            {/* Python */}
            <div className="example-block">
              <div className="code-block-header">
                <span>Python / Flask</span>
                <button
                  className="copy-btn"
                  onClick={() => copyToClipboard(pythonExample, 'python')}
                >
                  <FaCopy /> {copiedSection === 'python' ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <pre className="code-block">{pythonExample}</pre>
            </div>

            {/* PHP */}
            <div className="example-block">
              <div className="code-block-header">
                <span>PHP</span>
                <button
                  className="copy-btn"
                  onClick={() => copyToClipboard(phpExample, 'php')}
                >
                  <FaCopy /> {copiedSection === 'php' ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <pre className="code-block">{phpExample}</pre>
            </div>
          </div>
        </section>

        {/* Best Practices */}
        <section className="doc-section">
          <div className="section-header">
            <FaCheckCircle className="section-icon" />
            <h2>Best Practices</h2>
          </div>
          <div className="section-content">
            <ul className="best-practices-list">
              <li>
                <strong>Return 200 quickly:</strong> Process webhooks asynchronously. Return a 200 status immediately, then handle the event in a background job.
              </li>
              <li>
                <strong>Verify signatures:</strong> Always validate the <code>X-Webhook-Signature</code> header to ensure requests are from Astar.
              </li>
              <li>
                <strong>Use HTTPS:</strong> Webhook URLs must use HTTPS in production for security.
              </li>
              <li>
                <strong>Handle duplicates:</strong> Use the event ID or timestamp to detect and ignore duplicate deliveries.
              </li>
              <li>
                <strong>Log everything:</strong> Keep detailed logs of webhook receipts for debugging and auditing.
              </li>
              <li>
                <strong>Test thoroughly:</strong> Use the "Test Webhook" feature in Settings before going live.
              </li>
              <li>
                <strong>Monitor failures:</strong> Check the Webhook Events page regularly for failed deliveries.
              </li>
            </ul>
          </div>
        </section>

        {/* Response Codes */}
        <section className="doc-section">
          <div className="section-header">
            <FaExclamationTriangle className="section-icon" />
            <h2>Expected Response Codes</h2>
          </div>
          <div className="section-content">
            <p>Your webhook endpoint should return the following status codes:</p>
            <table className="headers-table">
              <thead>
                <tr>
                  <th>Status Code</th>
                  <th>Meaning</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                <tr className="success-row">
                  <td><code>200</code></td>
                  <td>Success</td>
                  <td>Webhook processed successfully</td>
                </tr>
                <tr className="error-row">
                  <td><code>4xx</code></td>
                  <td>Client Error</td>
                  <td>Astar will NOT retry (permanent failure)</td>
                </tr>
                <tr className="error-row">
                  <td><code>5xx</code></td>
                  <td>Server Error</td>
                  <td>Indicates your server had an issue</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Testing */}
        <section className="doc-section">
          <div className="section-header">
            <FaRocket className="section-icon" />
            <h2>Testing Webhooks</h2>
          </div>
          <div className="section-content">
            <p>We recommend using <a href="https://webhook.site" target="_blank" rel="noopener noreferrer">webhook.site</a> for testing:</p>
            <ol className="step-list">
              <li>Go to <a href="https://webhook.site" target="_blank" rel="noopener noreferrer">webhook.site</a></li>
              <li>Copy your unique URL</li>
              <li>Create a webhook in Astar with this URL</li>
              <li>Click "Test Webhook" to send a test event</li>
              <li>View the received payload on webhook.site</li>
              <li>Create a real order and watch the webhook deliver</li>
            </ol>
            
            <div className="info-box success">
              <FaCheckCircle />
              <div>
                <strong>Pro Tip:</strong> Once your webhook is working with webhook.site, replace the URL with your actual endpoint.
              </div>
            </div>
          </div>
        </section>

        {/* Troubleshooting */}
        <section className="doc-section">
          <div className="section-header">
            <FaExclamationTriangle className="section-icon" />
            <h2>Troubleshooting</h2>
          </div>
          <div className="section-content">
            <div className="troubleshooting-grid">
              <div className="trouble-item">
                <h4>Not receiving webhooks?</h4>
                <ul>
                  <li>Check webhook is Active (green toggle)</li>
                  <li>Verify URL is publicly accessible</li>
                  <li>Ensure your server returns 200 status</li>
                  <li>Check Webhook Events page for errors</li>
                </ul>
              </div>
              
              <div className="trouble-item">
                <h4>Getting HTTP 520 errors?</h4>
                <ul>
                  <li>Your server is unreachable</li>
                  <li>Check firewall settings</li>
                  <li>Verify URL is correct</li>
                  <li>Test with webhook.site first</li>
                </ul>
              </div>
              
              <div className="trouble-item">
                <h4>Signature verification failing?</h4>
                <ul>
                  <li>Ensure you're using the raw request body</li>
                  <li>Check you're using SHA-256</li>
                  <li>Verify secret matches exactly</li>
                  <li>Use timing-safe comparison</li>
                </ul>
              </div>
              
              <div className="trouble-item">
                <h4>Getting timeouts?</h4>
                <ul>
                  <li>Return 200 within 5 seconds</li>
                  <li>Process events asynchronously</li>
                  <li>Don't wait for external APIs</li>
                  <li>Use background jobs</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Support */}
        <section className="doc-section">
          <div className="section-header">
            <FaBook className="section-icon" />
            <h2>Need Help?</h2>
          </div>
          <div className="section-content">
            <p>
              Check the <strong>Webhook Events</strong> page to see all webhook deliveries and their responses. 
              This is your best tool for debugging issues.
            </p>
            <p>
              If you're still stuck, contact support with:
            </p>
            <ul>
              <li>Your webhook URL</li>
              <li>The event type you're testing</li>
              <li>Screenshot from Webhook Events page</li>
              <li>Your server logs showing the request</li>
            </ul>
          </div>
        </section>
      </div>
    </Container>
  );
};

export default WebhookDocumentation;
